import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Mail, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/utils';

interface DSR {
  id: string;
  type: 'ACCESS' | 'ERASURE' | 'RECTIFICATION' | 'PORTABILITY' | 'RESTRICTION' | 'OBJECTION';
  subjectEmail: string;
  subjectName: string | null;
  status: string;
  receivedAt: string;
  dueBy: string | null;
  completedAt: string | null;
  notes: string | null;
  reason: string | null;
}

const STATUSES = ['RECEIVED', 'IDENTITY_VERIFIED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'EXPIRED'] as const;

export function GdprDsrPage() {
  const qc = useQueryClient();
  const dsrs = useQuery({
    queryKey: ['gdpr', 'requests'],
    queryFn: async () => (await api.get('/api/gdpr/requests')).data,
  });

  const [selected, setSelected] = useState<DSR | null>(null);
  const [notes, setNotes] = useState('');
  const [nextStatus, setNextStatus] = useState<typeof STATUSES[number]>('IN_PROGRESS');

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await api.patch(`/api/gdpr/requests/${id}`, body)).data,
    onSuccess: () => {
      toast.success('DSR updated');
      qc.invalidateQueries({ queryKey: ['gdpr', 'requests'] });
      setSelected(null);
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  function openRequest(dsr: DSR) {
    setSelected(dsr);
    setNotes(dsr.notes ?? '');
    setNextStatus('IN_PROGRESS');
  }

  const total = dsrs.data?.items?.length ?? 0;
  const open = dsrs.data?.items?.filter((r: DSR) => r.status !== 'COMPLETED' && r.status !== 'REJECTED').length ?? 0;
  const overdue = dsrs.data?.items?.filter(
    (r: DSR) => r.dueBy && new Date(r.dueBy) < new Date() && r.status !== 'COMPLETED',
  ).length ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">GDPR data subject requests</h1>
        <p className="text-sm text-muted-foreground">
          Article 15 (access), 16 (rectification), 17 (erasure), 18 (restriction), 20 (portability),
          21 (objection). One-month response clock starts on receipt.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Stat icon={Mail} label="Total requests" value={total} />
        <Stat icon={Clock} label="Open" value={open} tone={open > 0 ? 'warning' : 'muted'} />
        <Stat icon={XCircle} label="Overdue" value={overdue} tone={overdue > 0 ? 'danger' : 'muted'} />
      </div>

      {dsrs.isLoading ? (
        <Skeleton className="h-64" />
      ) : !dsrs.data?.items?.length ? (
        <EmptyState icon={ShieldCheck} title="No requests yet" description="When a data subject submits a request, it will appear here with a 30-day SLA clock." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {(dsrs.data.items as DSR[]).map((r) => {
                const isOverdue = r.dueBy && new Date(r.dueBy) < new Date() && r.status !== 'COMPLETED';
                return (
                  <button
                    key={r.id}
                    onClick={() => openRequest(r)}
                    className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-accent/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{r.type}</Badge>
                        <span className="font-medium">{r.subjectEmail}</span>
                        {r.subjectName && <span className="text-xs text-muted-foreground">({r.subjectName})</span>}
                      </div>
                      {r.reason && (
                        <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{r.reason}</div>
                      )}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <div>Received {formatDate(r.receivedAt)}</div>
                      {r.dueBy && (
                        <div className={isOverdue ? 'text-destructive font-medium' : ''}>
                          {isOverdue ? 'Overdue: ' : 'Due '}
                          {formatDate(r.dueBy)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={r.status} />
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {selected && (
        <Dialog open onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selected.type} request · {selected.subjectEmail}</DialogTitle>
              <DialogDescription>
                Received {formatDate(selected.receivedAt)}. Current status: <Badge variant="secondary">{selected.status}</Badge>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {selected.reason && (
                <div>
                  <Label>Subject's reason</Label>
                  <p className="rounded-md border bg-muted/30 p-3 text-sm">{selected.reason}</p>
                </div>
              )}
              <div>
                <Label>Advance to status</Label>
                <select
                  value={nextStatus}
                  onChange={(e) => setNextStatus(e.target.value as typeof nextStatus)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Handler notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Actions taken, data exported, verification performed."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
              <Button
                onClick={() => update.mutate({ id: selected.id, body: { status: nextStatus, notes } })}
                disabled={update.isPending}
              >
                {update.isPending ? 'Saving...' : 'Update request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tone = 'muted',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone?: 'muted' | 'warning' | 'danger';
}) {
  const toneCls = {
    muted: 'bg-muted/40 text-muted-foreground',
    warning: 'bg-amber-500/10 text-amber-600',
    danger: 'bg-destructive/10 text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-2xl">{value}</div>
        </div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    RECEIVED: 'secondary',
    IDENTITY_VERIFIED: 'info',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    REJECTED: 'destructive',
    EXPIRED: 'destructive',
  };
  const Icon = status === 'COMPLETED' ? CheckCircle2 : status === 'REJECTED' || status === 'EXPIRED' ? XCircle : Clock;
  return (
    <Badge variant={v[status] ?? 'secondary'} className="shrink-0 gap-1">
      <Icon className="h-3 w-3" /> {status}
    </Badge>
  );
}
